import trLocale from '@fullcalendar/core/locales/tr';
import enLocale from '@fullcalendar/core/locales/en-gb';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import FullCalendar from '@fullcalendar/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchCalendarFeedEvents } from '../api/calendarFeeds';
import { EventCalendarDetailModal } from '../components/EventCalendarDetailModal';
import { EventCalendarMobileAgenda } from '../components/EventCalendarMobileAgenda';
import { EventCalendarSkeleton } from '../components/EventCalendarSkeleton';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { useAuth } from '../context/AuthContext';
import { CALENDAR_EVENTS } from '../data/calendarEvents';
import { selectionFromFullCalendarEvent } from '../utils/calendarEventSelection';

const MOBILE_CALENDAR_MQ = '(max-width: 991.98px)';

/**
 * Üye alanı — etkinlik takvimi (FullCalendar)
 * URL: /member-area/event-calendar
 *
 * Veri: `ClubEvent/GetAll` (turuncu) + `Meeting` (lacivert), Bearer token ile.
 * Simülasyon veya hata: örnek `CALENDAR_EVENTS`.
 */
export function EventCalendarPage() {
    const { t, i18n } = useTranslation();
    const { authToken, isSimulatedAuth } = useAuth();
    const calendarRef = useRef(/** @type {import('@fullcalendar/react').default | null} */(null));
    const [isMobileCalendar, setIsMobileCalendar] = useState(
        () =>
            typeof window !== 'undefined' &&
            window.matchMedia(MOBILE_CALENDAR_MQ).matches,
    );
    const [activeView, setActiveView] = useState(() =>
        typeof window !== 'undefined' &&
        window.matchMedia(MOBILE_CALENDAR_MQ).matches
            ? 'agenda'
            : 'dayGridMonth',
    );
    const useLiveApi = Boolean(authToken && !isSimulatedAuth);

    const [events, setEvents] = useState(
        /** @type {Record<string, unknown>[]} */ ([]),
    );
    const [loadError, setLoadError] = useState(
        /** @type {string | null} */ (null),
    );
    const [loading, setLoading] = useState(() =>
        Boolean(authToken && !isSimulatedAuth),
    );
    const [selectedEvent, setSelectedEvent] = useState(
        /** @type {import('../utils/calendarEventSelection').CalendarEventSelection | null} */ (
            null
        ),
    );

    const fullCalendarLocale = useMemo(
        () =>
            i18n.language?.toLowerCase().startsWith('en') ? enLocale : trLocale,
        [i18n.language],
    );

    const headerToolbar = useMemo(
        () =>
            isMobileCalendar
                ? {
                      left: 'prev,next',
                      center: 'title',
                      right: 'today',
                  }
                : {
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth,timeGridWeek,timeGridDay',
                  },
        [isMobileCalendar],
    );

    const showMobileAgenda = isMobileCalendar && activeView === 'agenda';

    const calendarHeight = useMemo(() => {
        if (!isMobileCalendar) return 680;
        return Math.min(520, Math.round(window.innerHeight * 0.52));
    }, [isMobileCalendar]);

    const changeCalendarView = useCallback((viewName) => {
        setActiveView(viewName);
        if (viewName === 'agenda') return;
        const api = calendarRef.current?.getApi();
        api?.changeView(viewName);
    }, []);

    const handleDatesSet = useCallback((arg) => {
        setActiveView(arg.view.type);
    }, []);

    const eventClassNames = useCallback((arg) => {
        const kind = arg.event.extendedProps?.kind;
        if (kind === 'weekly-meeting') return ['fc-event--weekly-meeting'];
        if (kind === 'event') return ['fc-event--event'];
        return [];
    }, []);

    const handleEventClick = useCallback((info) => {
        info.jsEvent.preventDefault();
        setSelectedEvent(selectionFromFullCalendarEvent(info.event));
    }, []);

    const closeDetailModal = useCallback(() => {
        setSelectedEvent(null);
    }, []);

    useEffect(() => {
        if (showMobileAgenda) return;
        const api = calendarRef.current?.getApi();
        if (!api) return;
        api.setOption('headerToolbar', headerToolbar);
        api.setOption('height', calendarHeight);
        api.setOption('dayMaxEvents', isMobileCalendar ? 2 : 3);
        api.setOption('aspectRatio', undefined);
    }, [headerToolbar, calendarHeight, isMobileCalendar, showMobileAgenda]);

    useEffect(() => {
        const mq = window.matchMedia(MOBILE_CALENDAR_MQ);
        const onChange = () => {
            const mobile = mq.matches;
            setIsMobileCalendar(mobile);
            setActiveView((prev) => {
                if (mobile && prev === 'dayGridMonth') return 'agenda';
                if (!mobile && prev === 'agenda') return 'dayGridMonth';
                return prev;
            });
        };
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        if (!authToken || isSimulatedAuth) {
            setEvents(CALENDAR_EVENTS);
            setLoadError(null);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError(null);

        (async () => {
            try {
                const next = await fetchCalendarFeedEvents(authToken);
                if (cancelled) return;
                setEvents(next);
                setLoadError(null);
            } catch (err) {
                if (cancelled) return;
                setEvents(CALENDAR_EVENTS);
                setLoadError(
                    err instanceof Error
                        ? err.message
                        : t('eventCalendarPage.genericLoadError'),
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authToken, isSimulatedAuth, t]);

    const showCalendarSkeleton = loading && useLiveApi;
    const calendarSkeletonVariant = showMobileAgenda
        ? 'agenda'
        : isMobileCalendar
          ? 'time'
          : 'month';

    return (
        <>
            <PageSeo
                title={`${t('eventCalendarPage.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.eventCalendar')}
                noindex
            />
            <SubpageLayout
                title={t('eventCalendarPage.title')}
                subtitle={t('eventCalendarPage.subtitle')}
                contentClassName="event-calendar-page"
            >
            {loadError ? (
                <p
                    className="text-center small text-secondary mb-3"
                    role="status"
                >
                    {t('eventCalendarPage.loadErrorNotice', {
                        error: loadError,
                    })}
                </p>
            ) : null}
            <div className="event-calendar-legend">
                <span className="event-calendar-legend-item">
                    <span
                        className="event-calendar-legend-swatch event-calendar-legend-swatch--meeting"
                        aria-hidden
                    />
                    {t('eventCalendarPage.legendMeeting')}
                </span>
                <span className="event-calendar-legend-item">
                    <span
                        className="event-calendar-legend-swatch event-calendar-legend-swatch--event"
                        aria-hidden
                    />
                    {t('eventCalendarPage.legendEvent')}
                </span>
            </div>
            {isMobileCalendar ? (
                <div
                    className="event-calendar-view-tabs"
                    role="tablist"
                    aria-label={t('eventCalendarPage.viewTabsAria')}
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeView === 'agenda'}
                        className={`event-calendar-view-tab${activeView === 'agenda' ? ' is-active' : ''}`}
                        onClick={() => changeCalendarView('agenda')}
                    >
                        {t('eventCalendarPage.viewList')}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeView === 'timeGridWeek'}
                        className={`event-calendar-view-tab${activeView === 'timeGridWeek' ? ' is-active' : ''}`}
                        onClick={() => changeCalendarView('timeGridWeek')}
                    >
                        {t('eventCalendarPage.viewWeek')}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeView === 'timeGridDay'}
                        className={`event-calendar-view-tab${activeView === 'timeGridDay' ? ' is-active' : ''}`}
                        onClick={() => changeCalendarView('timeGridDay')}
                    >
                        {t('eventCalendarPage.viewDay')}
                    </button>
                </div>
            ) : null}
            {showCalendarSkeleton ? (
                <div
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                    className={`event-calendar-mount event-calendar-mount--skeleton${isMobileCalendar ? ' event-calendar-mount--mobile' : ''}${showMobileAgenda ? ' event-calendar-mount--agenda' : isMobileCalendar ? ' event-calendar-mount--time-view' : ''}`}
                >
                    <span className="visually-hidden">
                        {t('eventCalendarPage.loading')}
                    </span>
                    <EventCalendarSkeleton variant={calendarSkeletonVariant} />
                </div>
            ) : showMobileAgenda ? (
                <div className="event-calendar-mount event-calendar-mount--mobile event-calendar-mount--agenda">
                    <EventCalendarMobileAgenda
                        events={events}
                        onEventSelect={setSelectedEvent}
                    />
                </div>
            ) : (
                <div
                    className={`event-calendar-mount${isMobileCalendar ? ' event-calendar-mount--mobile event-calendar-mount--time-view' : ''}`}
                >
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[
                            dayGridPlugin,
                            timeGridPlugin,
                            interactionPlugin,
                        ]}
                        initialView={
                            isMobileCalendar ? 'timeGridWeek' : 'dayGridMonth'
                        }
                        locale={fullCalendarLocale}
                        headerToolbar={headerToolbar}
                        datesSet={handleDatesSet}
                        events={events}
                        eventClassNames={eventClassNames}
                        eventClick={handleEventClick}
                        height={isMobileCalendar ? calendarHeight : 680}
                        navLinks
                        nowIndicator
                        dayMaxEvents={isMobileCalendar ? 2 : 3}
                        slotMinTime="08:00:00"
                        slotMaxTime="24:00:00"
                        scrollTime="09:00:00"
                        allDaySlot
                        handleWindowResize
                    />
                </div>
            )}
            <EventCalendarDetailModal
                open={Boolean(selectedEvent)}
                selection={selectedEvent}
                authToken={authToken}
                useLiveApi={useLiveApi}
                onClose={closeDetailModal}
            />
        </SubpageLayout>
        </>
    );
}
